import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useSearchParams } from "react-router-dom";
import { apiScopes } from "../auth/msalConfig";
import { useAppContext, type RouterInfo } from "../context/AppContext";
import { theme } from "../theme";

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;
const WS_BASE  = import.meta.env.VITE_WS_URL as string;

const RINGS = ["staging", "pilot", "production"] as const;
const RING_LABELS: Record<string, string> = {
  staging: "Test group",
  pilot: "Pilot group",
  production: "All devices",
};

interface RolloutInfo {
  ring: string;
  model_version: string;
  impulse_id: number | null;
  queued_by: string;
  queued_at: string;
}

interface ModelInfo {
  batch_id: number;
  name: string;
  model_version: string;
  impulse_id: number | null;
  created_at: string;
}

// Central releases are evaluated by the App Service, not deployed to devices.
// Keep this distinct from ModelInfo, which represents an edge package.
interface CentralModelInfo {
  version: string;
  status: string;
  activated_at: string | null;
}

// Reported by model_deploy.sh's report() calls (pending -> downloaded ->
// verified -> active, or failed at any step) — orchestrator/routers/
// deployments.py, one row per device (unique on serial).
interface DeploymentInfo {
  serial: string;
  model_version: string | null;
  ring: string | null;
  status: string;
  detail: string | null;
  updated_at: string;
}

const FOLLOW_RING = "__follow_ring__";

// model_version = build/artifact identity (bumped on every export, retrain or
// not). impulse_id = trained-model identity (only changes on an actual
// retrain). Show both consistently, everywhere a version appears.
function formatVersion(modelVersion: string, impulseId: number | null | undefined): string {
  return impulseId != null ? `v${impulseId} (build ${modelVersion})` : `build ${modelVersion}`;
}

// Shared uppercase "eyebrow" label style — the design system's caps tracking is
// what makes these read as labels rather than shouted text, so it is defined
// once here instead of being re-typed at each of the six call sites.
const EYEBROW: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: theme.textMuted,
};

function StatusDot({ online }: { online: boolean }) {
  return (
    <span
      className={online ? "dot-pulse" : undefined}
      style={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: online ? theme.success : theme.textMuted,
        flexShrink: 0,
      }}
    />
  );
}

/** Whether every device actually following this ring (no per-device override
 *  redirecting it elsewhere) has confirmed it's running this rollout's exact
 *  build. A ring with no such devices counts as fulfilled too — nothing left
 *  to wait for. Used so the top summary card stops reading "queued update"
 *  once it's actually been delivered, instead of staying stuck there forever
 *  (RingRollout rows are upserted permanently, there's no "delivered" flag
 *  server-side — this is derived client-side from the same per-device
 *  deployment telemetry the card badges already use). */
function rolloutFulfilled(ro: RolloutInfo, routers: RouterInfo[], deployments: DeploymentInfo[]): boolean | "unfollowed" {
  const following = routers.filter((r) => r.override_batch_id == null && r.ring === ro.ring);
  // Nobody follows this ring (every device in it is pinned elsewhere, or the
  // ring is empty): nothing can confirm the rollout, and saying "every device
  // confirmed" here hid a device stuck on a stale pin for weeks.
  if (following.length === 0) return "unfollowed";
  return following.every((r) => {
    const dep = deployments.find((d) => d.serial === r.serial_number);
    return dep?.status === "active" && dep.model_version === ro.model_version;
  });
}

/** What a device is supposed to be running right now: its pin if it has one,
 *  otherwise its rollout group's current build. null when nothing is queued. */
function expectedVersion(router: RouterInfo, rollouts: RolloutInfo[]): string | null {
  if (router.override_batch_id != null) return router.override_model_version;
  return rollouts.find((r) => r.ring === router.ring)?.model_version ?? null;
}

type RingState = "current" | "queued" | "unfollowed" | "other" | "none";

/** Where a given model stands in one rollout ring:
 *   none    — that ring has never had a rollout queued at all
 *   other   — the ring's current rollout is a DIFFERENT model
 *   queued  — this model IS the ring's current rollout, not yet confirmed
 *             running everywhere (rolloutFulfilled — same check the summary
 *             chips used to use on their own)
 *   current — this model IS the ring's rollout AND every following device
 *             confirmed it */
function ringStateForModel(
  ring: string, m: ModelInfo, rollouts: RolloutInfo[], routers: RouterInfo[], deployments: DeploymentInfo[]
): { state: RingState; rollout: RolloutInfo | null } {
  const ro = rollouts.find((r) => r.ring === ring) ?? null;
  if (!ro) return { state: "none", rollout: null };
  if (ro.model_version !== m.model_version) return { state: "other", rollout: ro };
  const fulfilled = rolloutFulfilled(ro, routers, deployments);
  return { state: fulfilled === "unfollowed" ? "unfollowed" : fulfilled ? "current" : "queued", rollout: ro };
}

const RING_STATE_COLOR: Record<RingState, string> = {
  current: theme.success,
  queued: theme.gold,
  unfollowed: theme.warning,
  other: theme.textMuted,
  none: theme.textMuted,
};

function RingDot({ state }: { state: RingState }) {
  const color = RING_STATE_COLOR[state];
  return (
    <span
      className={state === "queued" ? "dot-pulse" : undefined}
      style={{
        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
        background: state === "current" || state === "queued" ? color : "transparent",
        border: `1.5px solid ${color}`,
      }}
    />
  );
}

/** One built model, merged with its status across all three rollout rings —
 *  replaces the old side-by-side "queued update" chips + separate "Available
 *  models" list, which said the same thing about the same builds in two
 *  disconnected places. Collapsed: version + three small ring dots. Expanded
 *  (click the row): what each ring is actually doing with this model. */
function ModelCard({
  m, rollouts, routers, deployments, highlighted, onDeploy, deploying, onDelete, deleting, deleteError,
}: {
  m: ModelInfo; rollouts: RolloutInfo[]; routers: RouterInfo[]; deployments: DeploymentInfo[];
  /** Opened via /devices?model=ID (from a finished batch on Model Updates):
   *  start expanded, scroll into view, and outline it so the eye lands on it. */
  highlighted: boolean;
  onDeploy: (m: ModelInfo, ring: string) => Promise<void>;
  /** "<batch_id>:<ring>" of the rollout currently being queued, if any. */
  deploying: string | null;
  onDelete: (m: ModelInfo) => Promise<void>;
  /** True while this card's DELETE request is in flight. */
  deleting: boolean;
  /** The backend's refusal (409: still rolled out / still running somewhere), shown in the card. */
  deleteError: string | null;
}) {
  const [open, setOpen] = useState(highlighted);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlighted]);
  // Delete is a two-click action, same as "Stop training" on Model Updates:
  // the first click arms "Confirm delete", which disarms by itself after a few
  // seconds. (No window.confirm — browser dialogs are not used in this app.)
  const [deleteArmed, setDeleteArmed] = useState(false);
  useEffect(() => {
    if (!deleteArmed) return;
    const timer = window.setTimeout(() => setDeleteArmed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [deleteArmed]);
  useEffect(() => { setDeleteArmed(false); }, [deleting]);
  const ringStates = RINGS.map((ring) => ({ ring, ...ringStateForModel(ring, m, rollouts, routers, deployments) }));
  const anyActive = ringStates.some((rs) => rs.state === "current" || rs.state === "queued" || rs.state === "unfollowed");

  return (
    <div
      ref={ref}
      style={{
        background: theme.bgCard,
        // A model that some ring is actually on gets a green edge; the theme
        // colours are CSS vars, so a tint has to come from a *Soft token
        // rather than an appended alpha suffix (which silently voids the rule).
        border: `1px solid ${highlighted ? theme.gold : anyActive ? theme.success : theme.border}`,
        boxShadow: highlighted ? `0 0 0 3px ${theme.goldSoft}` : undefined,
        borderRadius: theme.radius,
        // Cards sit in a wrapping row: grow to fill a gap, but never below a
        // width that would wrap the version string.
        minWidth: 250,
        flex: "0 1 300px",
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "10px 14px", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 10,
        }}
      >
        <span style={{ color: theme.textMuted, fontSize: 9, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.12s" }}>
          ▶
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: theme.fontSizeXs, fontFamily: theme.fontMono,
            fontWeight: 700, color: theme.textPrimary,
          }}>
            {formatVersion(m.model_version, m.impulse_id)}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {m.name}
          </div>
        </div>
        <div style={{ display: "flex", gap: 5, flexShrink: 0 }} title="Test group / Pilot group / All devices">
          {ringStates.map(({ ring, state }) => <RingDot key={ring} state={state} />)}
        </div>
      </div>

      {open && (
        <div style={{ borderTop: `1px solid ${theme.border}`, padding: "10px 14px 12px" }}>
          <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6 }}>
            Built {new Date(m.created_at).toLocaleString("en-GB", {
              day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
            })}
          </div>
          {ringStates.map(({ ring, state, rollout }) => (
            <div key={ring} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", flexWrap: "wrap" }}>
              <RingDot state={state} />
              <span style={{ fontSize: theme.fontSize2Xs, fontWeight: 700, color: theme.textSecondary, minWidth: 84 }}>
                {RING_LABELS[ring] ?? ring}
              </span>
              <span style={{ fontSize: 11, color: RING_STATE_COLOR[state] }}>
                {state === "current" && "Current — every device confirmed"}
                {state === "queued" && "Queued — waiting for devices to pick it up"}
                {state === "unfollowed" && "Current rollout — but no device follows this group (all pinned to another model)"}
                {state === "other" && "Not the current build for this group"}
                {state === "none" && "Never deployed to this group"}
              </span>
              {rollout && state !== "other" && (
                <span style={{ fontSize: 11, color: theme.textMuted, marginLeft: "auto", whiteSpace: "nowrap" }}>
                  by {rollout.queued_by} · {new Date(rollout.queued_at).toLocaleString("en-GB", {
                    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              )}
              {/* Rolling out is this page's job (moved here from Model Updates,
                  where it was a batch stage). Any ring, any order: the groups
                  are independent, and a model can be re-deployed later. */}
              {(state === "none" || state === "other") && (
                <button
                  type="button"
                  disabled={deploying !== null}
                  onClick={(e) => { e.stopPropagation(); void onDeploy(m, ring); }}
                  style={{
                    marginLeft: rollout && state !== "other" ? 8 : "auto",
                    background: theme.bgCard, color: theme.goldText,
                    border: `1px solid ${theme.gold}`, borderRadius: 5, padding: "4px 10px",
                    fontSize: 11, fontWeight: 700, cursor: deploying ? "default" : "pointer",
                    opacity: deploying && deploying !== `${m.batch_id}:${ring}` ? 0.5 : 1,
                  }}
                >
                  {deploying === `${m.batch_id}:${ring}` ? "Deploying…" : `Deploy to ${RING_LABELS[ring] ?? ring}`}
                </button>
              )}
            </div>
          ))}
          {/* Removing a build the fleet no longer needs. The backend refuses
              while any group still offers it or a connected device still runs
              it, and says which — that message lands right here in the card. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={deleting || anyActive}
              onClick={(e) => {
                e.stopPropagation();
                if (deleteArmed) { setDeleteArmed(false); void onDelete(m); } else setDeleteArmed(true);
              }}
              title={anyActive
                ? "This build is a group's current rollout — deploy another model to that group first"
                : deleteArmed ? "Click again to delete this model" : "Delete this model (the batch and its history go with it)"}
              style={{
                marginLeft: "auto",
                background: deleteArmed ? theme.dangerSoft : "transparent",
                color: anyActive ? theme.textMuted : theme.danger,
                border: `1px solid ${deleteArmed ? theme.danger : theme.border}`, borderRadius: 5,
                padding: "3px 9px", fontSize: 11, fontWeight: 600,
                cursor: deleting || anyActive ? "default" : "pointer",
                opacity: deleting ? 0.6 : 1,
              }}
            >
              {deleting ? "Deleting…" : deleteArmed ? "Confirm delete" : "Delete model"}
            </button>
            {deleteError && (
              <span role="alert" style={{ flexBasis: "100%", fontSize: 11, color: theme.danger }}>
                {deleteError}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Colour-coded per-device rollout status, from the model_deploy.sh agent's
 *  own reports — not to be confused with the model badge above it (which
 *  shows what the device says it's *actually* running). "Up to date" vs
 *  "Version mismatch" specifically compares the two: the deploy agent can
 *  report "active" on a version the device isn't really running yet (a race,
 *  or a swap that silently didn't take — see ips_ml_runner #784). */
function DeployStatusBadge({ deployment, router, expected }: { deployment: DeploymentInfo | undefined; router: RouterInfo; expected: string | null }) {
  if (!deployment) return null;

  // `soft` is passed alongside `color` because the palette is CSS custom
  // properties: an alpha suffix appended to a var() is not a colour, so the
  // tinted fill has to come from the matching *Soft token.
  const pill = (text: string, color: string, soft: string, pulse = false): React.ReactElement => (
    <span
      title={deployment.detail ?? undefined}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
        padding: "3px 10px", borderRadius: 11,
        color, background: soft, border: `1px solid ${color}`,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {pulse && <Spinner color={color} />}
      {text}
    </span>
  );

  if (deployment.status === "failed") {
    return pill(`Update failed${deployment.model_version ? ` (v${deployment.model_version})` : ""}`, theme.danger, theme.dangerSoft);
  }
  if (deployment.status === "pending" || deployment.status === "downloaded" || deployment.status === "verified") {
    return pill(`Updating… (${deployment.status})`, theme.goldText, theme.goldSoft, true);
  }
  if (deployment.status === "active") {
    const mismatch =
      router.model_version != null &&
      deployment.model_version != null &&
      router.model_version !== deployment.model_version;
    if (mismatch) {
      return pill(`Mismatch: deployed v${deployment.model_version}, running v${router.model_version}`, theme.warning, theme.warningSoft);
    }
    // "Up to date" used to mean "the last thing the agent reported is what runs"
    // — true for a device that last reported in July and has been ignoring its
    // group's rollout ever since. It has to mean "running what it should".
    if (expected != null && router.model_version != null && router.model_version !== expected) {
      return pill(`Outdated: running build ${router.model_version}, should run build ${expected}`, theme.warning, theme.warningSoft);
    }
    return pill("Up to date", theme.success, theme.successSoft);
  }
  return null;
}

function Spinner({ color }: { color: string }) {
  return (
    <span
      className="spin"
      style={{
        width: 9, height: 9, borderRadius: "50%",
        border: `1.5px solid ${theme.border}`, borderTopColor: color, flexShrink: 0,
      }}
    />
  );
}

/** Section heading shared by the Models and Devices collections, so the two
 *  read as clearly separate groups with a consistent title / hint / count. */
function SectionHeader({ title, hint, count }: { title: string; hint?: string; count?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.textPrimary, letterSpacing: "0.01em" }}>
        {title}
      </h2>
      {count && (
        <span style={{ fontSize: theme.fontSize2Xs, color: theme.textMuted }}>{count}</span>
      )}
      {hint && (
        <span style={{ fontSize: theme.fontSize2Xs, color: theme.textMuted }}>— {hint}</span>
      )}
    </div>
  );
}

/** Online/offline as a pill, so device state reads at a glance instead of as
 *  a bare word lost in a busy row. */
function OnlinePill({ online }: { online: boolean }) {
  // Offline is deliberately unfilled and grey: it is the resting state, not an
  // alarm, so only "online" carries a tint. (Neither uses the gold accent.)
  const color = online ? theme.success : theme.textMuted;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
        padding: "3px 10px", borderRadius: 11,
        color, background: online ? theme.successSoft : "transparent",
        border: `1px solid ${online ? theme.success : theme.border}`,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      <StatusDot online={online} />
      {online ? "Online" : "Offline"}
    </span>
  );
}

/** A labelled control in the settings footer — label stacked above its input so
 *  the three editors line up as a scannable form instead of a run-on sentence. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={EYEBROW}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>{children}</div>
    </div>
  );
}

/** One number in the fleet summary strip. `sub` is the qualifier that gives the
 *  number meaning (how many of them are online, how many still pending) — it
 *  carries the only colour in the tile, so the strip reads green/amber at a
 *  glance without any tile becoming a coloured block. */
function StatTile({ label, value, sub, subColor }: { label: string; value: string; sub: string; subColor: string }) {
  return (
    <div
      style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius,
        padding: "14px 16px",
        display: "flex", flexDirection: "column", gap: 4,
        minWidth: 0,
      }}
    >
      <span style={EYEBROW}>{label}</span>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: theme.textPrimary, lineHeight: 1.15 }}>{value}</span>
        <span style={{ fontSize: theme.fontSize2Xs, color: subColor }}>{sub}</span>
      </div>
    </div>
  );
}

function TrashIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function RouterRow({
  router,
  models,
  rollouts,
  deployment,
  onDelete,
  onPingIntervalSaved,
  onRingSaved,
  onOverrideSaved,
}: {
  router: RouterInfo;
  models: ModelInfo[];
  rollouts: RolloutInfo[];
  deployment: DeploymentInfo | undefined;
  onDelete: (id: number) => void;
  onPingIntervalSaved: (id: number, interval: number) => void;
  onRingSaved: (id: number, ring: string) => void;
  onOverrideSaved: (id: number, patch: Partial<RouterInfo>) => void;
}) {
  const { instance, accounts } = useMsal();
  const [pingInterval, setPingInterval] = useState(router.ping_interval);
  const [saving, setSaving] = useState(false);
  const [ringSaving, setRingSaving] = useState(false);
  const [overrideSaving, setOverrideSaving] = useState(false);

  const isDirty = pingInterval !== router.ping_interval;

  const handleRingChange = async (ring: string) => {
    setRingSaving(true);
    try {
      const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const resp = await fetch(`${API_BASE}/api/devices/${router.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${r.accessToken}`,
        },
        body: JSON.stringify({ ring }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      onRingSaved(router.id, ring);
    } catch (err) {
      console.error("Failed to save rollout ring:", err);
    } finally {
      setRingSaving(false);
    }
  };

  const handleOverrideChange = async (value: string) => {
    setOverrideSaving(true);
    try {
      const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const authHeader = { Authorization: `Bearer ${r.accessToken}` };
      if (value === FOLLOW_RING) {
        const resp = await fetch(`${API_BASE}/api/devices/${router.id}/model_override`, {
          method: "DELETE",
          headers: authHeader,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        onOverrideSaved(router.id, {
          override_batch_id: null, override_model_version: null,
          override_impulse_id: null,
          override_by: null, override_at: null,
        });
      } else {
        const assignedBy = accounts[0]?.name || accounts[0]?.username || "operator";
        const resp = await fetch(`${API_BASE}/api/devices/${router.id}/model_override`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeader },
          body: JSON.stringify({ batch_id: Number(value), assigned_by: assignedBy }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const updated = await resp.json();
        onOverrideSaved(router.id, {
          override_batch_id: updated.override_batch_id,
          override_model_version: updated.override_model_version,
          override_impulse_id: updated.override_impulse_id,
          override_by: updated.override_by,
          override_at: updated.override_at,
        });
      }
    } catch (err) {
      console.error("Failed to save model override:", err);
    } finally {
      setOverrideSaving(false);
    }
  };

  const lastSeenLabel = router.last_seen
    ? new Date(router.last_seen).toLocaleString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "Never";

  const handleSavePingInterval = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const resp = await fetch(`${API_BASE}/api/devices/${router.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${r.accessToken}`,
        },
        body: JSON.stringify({ ping_interval: pingInterval }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      onPingIntervalSaved(router.id, pingInterval);
    } catch (err) {
      console.error("Failed to save ping interval:", err);
      // revert on error
      setPingInterval(router.ping_interval);
    } finally {
      setSaving(false);
    }
  };

  const overrideActive = router.override_batch_id != null;
  // A pin to a batch that is no longer in the model list (deleted before
  // deletions started clearing pins). Without its own option the <select>
  // silently fell back to "Follow …" and the pin was invisible.
  const overrideStale = overrideActive && !models.some((m) => m.batch_id === router.override_batch_id);
  const expected = expectedVersion(router, rollouts);

  return (
    <div
      style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius,
        overflow: "hidden",
        opacity: router.online ? 1 : 0.82,
      }}
    >
      {/* ── Header: identity + state ─────────────────────────────── */}
      {/* Wraps rather than squeezing: at narrow widths the pill and last-seen
          block drop to their own line instead of crushing the serial. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div
            style={{
              fontFamily: theme.fontMono,
              fontSize: theme.fontSizeSm,
              fontWeight: 700,
              color: theme.textPrimary,
              letterSpacing: "0.03em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {router.serial_number}
          </div>
          {router.name && (
            <div style={{ fontSize: theme.fontSizeXs, color: theme.textSecondary, marginTop: 2 }}>
              {router.name}
            </div>
          )}
        </div>

        <OnlinePill online={router.online} />

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={EYEBROW}>Last seen</div>
          <div style={{ fontSize: theme.fontSize2Xs, color: theme.textSecondary, marginTop: 2 }}>{lastSeenLabel}</div>
        </div>

        <button
          onClick={() => onDelete(router.id)}
          title="Delete device"
          style={{
            background: "transparent",
            border: `1px solid ${theme.border}`,
            color: theme.textMuted,
            borderRadius: 5,
            padding: "6px 10px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            transition: "border-color 0.15s, color 0.15s",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = theme.danger;
            (e.currentTarget as HTMLButtonElement).style.color = theme.danger;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.borderColor = theme.border;
            (e.currentTarget as HTMLButtonElement).style.color = theme.textMuted;
          }}
        >
          <TrashIcon />
        </button>
      </div>

      {/* ── Running model: what the device reports it's actually on ── */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          padding: "0 18px 14px",
        }}
      >
        <span style={EYEBROW}>Running model</span>
        {router.model_name || router.model_version ? (
          <>
            {router.model_name && (
              <span style={{ fontSize: theme.fontSizeXs, color: theme.textSecondary, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {router.model_name}
              </span>
            )}
            {router.model_version && (
              <span
                style={{
                  fontSize: 11, fontFamily: theme.fontMono, fontWeight: 700,
                  color: theme.textSecondary, background: theme.bgInput,
                  border: `1px solid ${theme.border}`, borderRadius: 4, padding: "2px 7px",
                }}
              >
                {formatVersion(router.model_version, router.impulse_id)}
              </span>
            )}
            <DeployStatusBadge deployment={deployment} router={router} expected={expected} />
          </>
        ) : (
          <span style={{ fontSize: theme.fontSize2Xs, color: theme.textMuted, fontStyle: "italic" }}>
            no model reported yet
          </span>
        )}
      </div>

      {/* ── Settings footer: the editable controls, grouped as a form ── */}
      <div
        style={{
          display: "flex", alignItems: "flex-end", gap: 22, flexWrap: "wrap",
          padding: "12px 18px", background: theme.bgInput,
          borderTop: `1px solid ${theme.border}`,
        }}
      >
        <Field label="Ping interval (s)">
          <input
            type="number"
            min={10}
            max={300}
            value={pingInterval}
            onChange={(e) => setPingInterval(Number(e.target.value))}
            onKeyDown={(e) => { if (e.key === "Enter") handleSavePingInterval(); }}
            style={{
              width: 64, padding: "6px 8px", fontSize: theme.fontSizeXs, fontWeight: 600,
              fontFamily: theme.fontSans, borderRadius: 5,
              // The gold edge is the only cue that an edit is unsaved, so it
              // stays even though every other control uses the neutral border.
              border: `1px solid ${isDirty ? theme.gold : theme.borderStrong}`,
              background: theme.bgCard, color: theme.textPrimary, outline: "none",
              textAlign: "center",
            }}
          />
          {isDirty && (
            <button
              disabled={saving}
              onClick={handleSavePingInterval}
              style={{
                padding: "6px 12px", fontSize: theme.fontSize2Xs, fontWeight: 700, borderRadius: 5,
                border: "none", background: theme.gold,
                color: theme.onGold, cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1, transition: "opacity 0.15s", whiteSpace: "nowrap",
              }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </Field>

        <Field label="Rollout group">
          <select
            value={router.ring}
            disabled={ringSaving || overrideActive}
            title={overrideActive ? "This device has a pinned model override, so its rollout group is not applied" : undefined}
            onChange={(e) => handleRingChange(e.target.value)}
            style={{
              padding: "6px 8px", fontSize: theme.fontSizeXs, fontWeight: 600,
              fontFamily: theme.fontSans, borderRadius: 5,
              border: `1px solid ${theme.borderStrong}`, background: theme.bgCard,
              color: overrideActive ? theme.textMuted : theme.textPrimary, outline: "none",
              cursor: ringSaving || overrideActive ? "not-allowed" : "pointer",
              opacity: overrideActive ? 0.6 : 1,
            }}
          >
            {RINGS.map((ring) => (
              <option key={ring} value={ring}>{RING_LABELS[ring]}</option>
            ))}
          </select>
        </Field>

        <Field label="Model on this device">
          <select
            value={overrideActive ? String(router.override_batch_id) : FOLLOW_RING}
            disabled={overrideSaving}
            onChange={(e) => handleOverrideChange(e.target.value)}
            style={{
              padding: "6px 8px", fontSize: theme.fontSizeXs, fontWeight: 600,
              fontFamily: theme.fontSans, borderRadius: 5,
              // Gold edge marks a device pinned off its rollout group.
              border: `1px solid ${overrideStale ? theme.warning : overrideActive ? theme.gold : theme.borderStrong}`,
              background: theme.bgCard, color: theme.textPrimary, outline: "none",
              cursor: overrideSaving ? "not-allowed" : "pointer", maxWidth: 240,
            }}
          >
            <option value={FOLLOW_RING}>Follow {RING_LABELS[router.ring] ?? router.ring}</option>
            {overrideStale && (
              <option value={String(router.override_batch_id)}>
                Pinned to a removed model (batch #{router.override_batch_id}
                {router.override_model_version ? `, build ${router.override_model_version}` : ""})
              </option>
            )}
            {models.map((m) => (
              <option key={m.batch_id} value={m.batch_id}>{formatVersion(m.model_version, m.impulse_id)}</option>
            ))}
          </select>
          {overrideActive && router.override_by && (
            <span style={{ fontSize: 11, color: theme.textMuted, whiteSpace: "nowrap" }}>
              pinned by {router.override_by}
            </span>
          )}
        </Field>
      </div>
    </div>
  );
}

export function DevicesPage() {
  const { instance, accounts } = useMsal();
  const { routers, setRouters } = useAppContext();
  const [rollouts, setRollouts] = useState<RolloutInfo[]>([]);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [centralModels, setCentralModels] = useState<CentralModelInfo[]>([]);
  const [deployments, setDeployments] = useState<DeploymentInfo[]>([]);
  const [deploying, setDeploying] = useState<string | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deletingModel, setDeletingModel] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<{ batchId: number; message: string } | null>(null);
  // /devices?model=<batch_id> — the hand-off from a finished batch on Model
  // Updates: that model's card opens and is outlined.
  const [searchParams] = useSearchParams();
  const highlightedModel = Number(searchParams.get("model")) || null;

  const handleDeploy = async (m: ModelInfo, ring: string) => {
    setDeployError(null);
    setDeploying(`${m.batch_id}:${ring}`);
    try {
      const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const authHeader = { Authorization: `Bearer ${r.accessToken}`, "Content-Type": "application/json" };
      // The rollout itself is still the orchestrator's approval gate for the
      // ring (it writes the RingRollout the devices poll); only the button
      // moved here. The batch's stage is no longer touched by it.
      const resp = await fetch(`${API_BASE}/orchestrator/api/batches/${m.batch_id}/approvals`, {
        method: "POST", headers: authHeader,
        body: JSON.stringify({
          gate: ring, decision: "approved",
          approver: accounts[0]?.username || accounts[0]?.name || "operator",
          note: `deployed from Devices`,
        }),
      });
      if (!resp.ok) {
        const detail = await resp.json().then((j: { detail?: string }) => j.detail).catch(() => null);
        throw new Error(detail ?? `HTTP ${resp.status}`);
      }
      const ro = await fetch(`${API_BASE}/orchestrator/api/rollouts`, { headers: authHeader });
      if (ro.ok) setRollouts(await ro.json());
    } catch (err) {
      setDeployError(`Deploying ${formatVersion(m.model_version, m.impulse_id)} to ${RING_LABELS[ring] ?? ring} failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDeploying(null);
    }
  };

  // Shared by the DELETE response and the WS "batch_deleted" frame (another
  // operator, or the Model Updates page, may have deleted it): the card goes,
  // and devices that were pinned to it read as following their ring again.
  const removeModel = (batchId: number) => {
    setModels((prev) => prev.filter((x) => x.batch_id !== batchId));
    setRouters((prev) => prev.map((r) => (r.override_batch_id === batchId
      ? { ...r, override_batch_id: null, override_model_version: null, override_impulse_id: null, override_by: null, override_at: null }
      : r)));
  };

  // Drops a model from the fleet's list. The batch behind it is deleted (its
  // history too) and any device pinned to it via a per-device override goes
  // back to following its ring. The backend answers 409 while a group still
  // offers the build or a connected device still runs it; that text is shown
  // in the card so the operator knows which group to re-deploy first.
  const handleDeleteModel = async (m: ModelInfo) => {
    setDeleteError(null);
    setDeletingModel(m.batch_id);
    try {
      const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const resp = await fetch(`${API_BASE}/orchestrator/api/models/${m.batch_id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${r.accessToken}` },
      });
      if (!resp.ok) {
        const detail = await resp.json().then((j: { detail?: string }) => j.detail).catch(() => null);
        throw new Error(detail ?? `HTTP ${resp.status}`);
      }
      removeModel(m.batch_id);
    } catch (err) {
      setDeleteError({ batchId: m.batch_id, message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDeletingModel(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
        const authHeader = { Authorization: `Bearer ${r.accessToken}` };
        const [rolloutsResp, modelsResp, centralModelsResp, deploymentsResp] = await Promise.all([
          fetch(`${API_BASE}/orchestrator/api/rollouts`, { headers: authHeader }),
          fetch(`${API_BASE}/orchestrator/api/models`, { headers: authHeader }),
          fetch(`${API_BASE}/orchestrator/api/central-models`, { headers: authHeader }),
          fetch(`${API_BASE}/orchestrator/api/deployments`, { headers: authHeader }),
        ]);
        if (!rolloutsResp.ok) throw new Error(`HTTP ${rolloutsResp.status}`);
        if (!modelsResp.ok) throw new Error(`HTTP ${modelsResp.status}`);
        if (!centralModelsResp.ok) throw new Error(`HTTP ${centralModelsResp.status}`);
        if (!deploymentsResp.ok) throw new Error(`HTTP ${deploymentsResp.status}`);
        setRollouts(await rolloutsResp.json());
        setModels(await modelsResp.json());
        setCentralModels(await centralModelsResp.json());
        setDeployments(await deploymentsResp.json());
      } catch (err) {
        console.error("Failed to load rollout/model status:", err);
      }
    })();
  }, [instance, accounts]);

  // Live update as model_deploy.sh's agent reports each step (pending ->
  // downloaded -> verified -> active/failed). deployments.py broadcasts this
  // on the orchestrator's OWN WS channel (/orchestrator/ws) — deliberately
  // separate from the main /ws/detections one AppContext.lastMessage carries
  // (see orchestrator/ws_manager.py), so this page needs its own connection
  // rather than reusing the app-level one. Reconnects with backoff, same as
  // OrchestratorPage — Azure App Service can drop a long-idle WS.
  useEffect(() => {
    let cancelled = false;
    let ws: WebSocket | null = null;
    let retryDelay = 1000;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      if (cancelled || !accounts.length) return;
      try {
        const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
        if (cancelled) return;
        ws = new WebSocket(`${WS_BASE}/orchestrator/ws?token=${encodeURIComponent(r.accessToken)}`);
        ws.onopen = () => { retryDelay = 1000; };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "deployment_updated" && msg.data) {
              const d = msg.data as DeploymentInfo;
              setDeployments((prev) => {
                const exists = prev.some((p) => p.serial === d.serial);
                return exists ? prev.map((p) => (p.serial === d.serial ? d : p)) : [...prev, d];
              });
            } else if (msg.type === "batch_deleted" && typeof msg.data?.id === "number") {
              removeModel(msg.data.id);
            }
          } catch {
            // ignore malformed frames
          }
        };
        ws.onclose = () => {
          if (cancelled) return;
          retryTimer = setTimeout(connect, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
        };
      } catch (err) {
        console.error("Devices WS auth failed:", err);
        if (!cancelled) retryTimer = setTimeout(connect, retryDelay);
      }
    };
    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws?.close();
    };
  }, [instance, accounts]);

  const handleDelete = async (id: number) => {
    try {
      const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
      const resp = await fetch(`${API_BASE}/api/devices/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${r.accessToken}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setRouters((prev) => prev.filter((router) => router.id !== id));
    } catch (err) {
      console.error("Failed to delete device:", err);
    }
  };

  const handlePingIntervalSaved = (id: number, interval: number) => {
    setRouters((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ping_interval: interval } : r))
    );
  };

  const handleRingSaved = (id: number, ring: string) => {
    setRouters((prev) => prev.map((r) => (r.id === id ? { ...r, ring } : r)));
  };

  const handleOverrideSaved = (id: number, patch: Partial<RouterInfo>) => {
    setRouters((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  // Fleet summary numbers — all derived from state this page already loads, so
  // the strip costs no extra request. "Up to date" counts devices whose deploy
  // agent reported `active` on the same build the device says it is running;
  // anything else (mid-update, failed, mismatched) counts as outstanding.
  const onlineCount = routers.filter((r) => r.online).length;
  const upToDateCount = routers.filter((r) => {
    const dep = deployments.find((d) => d.serial === r.serial_number);
    const expected = expectedVersion(r, rollouts);
    return dep?.status === "active"
      && (dep.model_version == null || dep.model_version === r.model_version)
      && (expected == null || r.model_version === expected);
  }).length;
  const outstanding = routers.length - upToDateCount;
  const activeCentralModel = centralModels.find((model) => model.status === "active");

  return (
    <div style={{ padding: 28, maxWidth: 980 }}>
      {/* No page title, matching Detections and Live Stream: the rail names the
          view, and the section headers below already say what each block is. */}

      {/* ── Fleet summary ──────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: 12,
          marginBottom: 26,
        }}
      >
        <StatTile
          label="Devices"
          value={String(routers.length)}
          sub={`${onlineCount} online`}
          subColor={onlineCount > 0 ? theme.success : theme.textMuted}
        />
        <StatTile
          label="Models built"
          value={String(models.length)}
          sub={rollouts.length > 0 ? `${rollouts.length} rolling out` : "none rolling out"}
          subColor={rollouts.length > 0 ? theme.goldText : theme.textMuted}
        />
        <StatTile
          label="Central model"
          value={activeCentralModel ? `ID: ${activeCentralModel.version}` : "Baseline"}
          sub={activeCentralModel ? "active" : "no retrained model active"}
          subColor={activeCentralModel ? theme.success : theme.textMuted}
        />
        <StatTile
          label="Up to date"
          value={routers.length > 0 ? `${upToDateCount}/${routers.length}` : "0"}
          sub={routers.length === 0
            ? "no devices yet"
            : outstanding > 0 ? `${outstanding} outstanding` : "all confirmed"}
          subColor={outstanding > 0 ? theme.warning : theme.textMuted}
        />
      </div>

      {/* ── Models section ─────────────────────────────────────────── */}
      {models.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <SectionHeader
            title="Models"
            hint="click a model to see its status per rollout group"
            count={`${models.length} built`}
          />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
            {models.map((m) => (
              <ModelCard
                key={m.batch_id} m={m} rollouts={rollouts} routers={routers} deployments={deployments}
                highlighted={m.batch_id === highlightedModel}
                onDeploy={handleDeploy} deploying={deploying}
                onDelete={handleDeleteModel} deleting={deletingModel === m.batch_id}
                deleteError={deleteError?.batchId === m.batch_id ? deleteError.message : null}
              />
            ))}
          </div>
          {deployError && (
            <div role="alert" style={{ marginTop: 10, color: theme.danger, fontSize: theme.fontSize2Xs }}>{deployError}</div>
          )}
        </section>
      )}

      {/* ── Devices section ────────────────────────────────────────── */}
      {/* The rule above the heading separates "which model" (above) from
          "which device" (below), so the two collections read as distinct. */}
      <section style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 22 }}>
        <SectionHeader
          title="Devices"
          hint="registered routers and their per-device settings"
          count={routers.length > 0
            ? `${routers.length} device${routers.length !== 1 ? "s" : ""} · ${onlineCount} online`
            : undefined}
        />

      {routers.length === 0 ? (
        // Bordered panel rather than floating text, so "nothing here yet" looks
        // like a deliberate state and not a half-rendered page.
        <div
          style={{
            border: `1px dashed ${theme.border}`,
            borderRadius: theme.radius,
            background: theme.bgCard,
            padding: "44px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            textAlign: "center",
            color: theme.textMuted,
          }}
        >
          <span style={{ fontSize: 32, color: theme.textMuted }}>⊘</span>
          <span style={{ fontSize: theme.fontSizeSm, fontWeight: 700, color: theme.textSecondary }}>
            No router devices registered yet
          </span>
          <span style={{ fontSize: theme.fontSize2Xs, color: theme.textMuted }}>
            Devices appear automatically when they connect or send data.
          </span>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {routers.map((router) => (
            <RouterRow
              key={router.id}
              router={router}
              models={models}
              rollouts={rollouts}
              deployment={deployments.find((d) => d.serial === router.serial_number)}
              onDelete={handleDelete}
              onPingIntervalSaved={handlePingIntervalSaved}
              onRingSaved={handleRingSaved}
              onOverrideSaved={handleOverrideSaved}
            />
          ))}
        </div>
      )}
      </section>
    </div>
  );
}
